import { z } from 'zod';
import { BandScoreSchema, SpeakingCriteriaSchema } from './sessions.js';
// ---------------------------------------------------------------------------
// IELTS Speaking parts
// ---------------------------------------------------------------------------
export const SpeakingPartSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
]);
export const SpeakingModeSchema = z.enum(['full_test', 'warm_up', 'section']);
export const StartSpeakingSessionRequestSchema = z.object({
    mode: SpeakingModeSchema.optional().default('full_test'),
    part: SpeakingPartSchema.optional(),
});
// What the server tells the client to do next. The client renders different UI
// per phase (e.g. show cue card for `cue_card_prep`, show "Listening…" for
// `examiner_speaking`, show big mic button for `candidate_speaking`).
export const SpeakingPhaseSchema = z.enum([
    'examiner_speaking', // examiner audio is being delivered/played
    'candidate_speaking', // mic should be open, user is answering
    'cue_card_prep', // Part 2 only: 1 minute of silent prep with a cue card
    'finished', // the whole speaking test is done
]);
// ---------------------------------------------------------------------------
// Examiner turn (sent from server -> client)
// ---------------------------------------------------------------------------
export const CueCardSchema = z.object({
    topic: z.string(),
    bullets: z.array(z.string()).min(2).max(5),
});
// Authoritative, server-computed position info. Used by the client to drive
// the segmented Part 1 / Part 2 / Part 3 progress bar without having to know
// the script structure (which lives only on the server). `indexInPart` is
// 0-based: the 1st question of Part 1 is index 0, the cue card is index 0
// of Part 2, etc.
export const ExaminerTurnProgressSchema = z.object({
    part: SpeakingPartSchema,
    indexInPart: z.number().int().min(0),
    totalInPart: z.number().int().positive(),
});
// One thing the examiner says to the candidate. The client plays `audio` (a
// base64 wav/mp3) while showing `text` as a transcript. `cue_card` is only
// present at the start of Part 2.
export const ExaminerTurnSchema = z.object({
    text: z.string(),
    audioBase64: z.string(),
    audioMimeType: z.string(), // e.g. 'audio/mpeg'
    part: SpeakingPartSchema,
    questionIndex: z.number().int().min(0),
    cueCard: CueCardSchema.optional(),
    // How long the candidate is *expected* to speak after this turn, in seconds.
    // Just a UI hint — Part 1: ~20s, Part 2: 90-120s, Part 3: ~40s.
    expectedAnswerSeconds: z.number().int().positive(),
    // Server-authoritative progress. Drives the per-part progress bar.
    progress: ExaminerTurnProgressSchema,
});
// ---------------------------------------------------------------------------
// Start a speaking session
// ---------------------------------------------------------------------------
export const StartSpeakingSessionResponseSchema = z.object({
    sessionId: z.string().uuid(),
    phase: SpeakingPhaseSchema,
    examinerTurn: ExaminerTurnSchema,
});
// ---------------------------------------------------------------------------
// Submit a candidate turn (audio in -> next examiner turn out)
// ---------------------------------------------------------------------------
// The candidate-submitted audio. We don't accept a JSON body for this; the
// client uploads multipart/form-data with `audio` (Blob). Keeping this schema
// here for documentation only.
export const SubmitTurnFieldsSchema = z.object({
    // Optional: the position the candidate thinks they're answering. Useful for
    // detecting client/server desync. The server's stored position wins.
    expectedPosition: z.coerce.number().int().min(0).optional(),
});
export const SubmitTurnResponseSchema = z.object({
    sessionId: z.string().uuid(),
    // The transcript Whisper produced for the candidate's audio.
    candidateTranscript: z.string(),
    phase: SpeakingPhaseSchema,
    // Present unless `phase === 'finished'`.
    examinerTurn: ExaminerTurnSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Finish (and score) a session
// ---------------------------------------------------------------------------
export const FinishSpeakingSessionResponseSchema = z.object({
    sessionId: z.string().uuid(),
    overallBand: BandScoreSchema,
    criteria: SpeakingCriteriaSchema,
    summary: z.string(),
    strengths: z.array(z.string()),
    improvements: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Get a finished session (for the results screen)
// ---------------------------------------------------------------------------
export const SpeakingTranscriptEntrySchema = z.object({
    position: z.number().int().min(0),
    part: SpeakingPartSchema,
    examinerText: z.string(),
    candidateTranscript: z.string().nullable(),
    // True when the candidate's "answer" is a server-side canned placeholder
    // (created by the dev /skip-turn endpoint). The UI should render these
    // distinctly so a tester doesn't mistake them for words they actually said.
    skipped: z.boolean().optional().default(false),
});
export const SpeakingResultsSchema = z.object({
    sessionId: z.string().uuid(),
    status: z.enum(['in_progress', 'completed', 'abandoned']),
    overallBand: BandScoreSchema.nullable(),
    criteria: SpeakingCriteriaSchema.nullable(),
    summary: z.string().nullable(),
    strengths: z.array(z.string()),
    improvements: z.array(z.string()),
    transcript: z.array(SpeakingTranscriptEntrySchema),
});
// ---------------------------------------------------------------------------
// Completed speaking sessions list (Results tab)
// ---------------------------------------------------------------------------
export const SpeakingHistoryItemSchema = z.object({
    sessionId: z.string().uuid(),
    completedAt: z.string().datetime(),
    overallBand: BandScoreSchema.nullable(),
    kind: z.enum(['warm_up', 'section', 'full_test']),
});
export const SpeakingHistoryResponseSchema = z.object({
    sessions: z.array(SpeakingHistoryItemSchema),
});
